import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_NUMBER_SCHEME,
  NUMBER_KIND_LABELS,
  NUMBER_KINDS,
  NUMBER_SCHEME_LABELS,
  NUMBER_SCHEMES,
  NUMBER_START_MAX,
  formatNumberByScheme,
  type DocumentNumberKind,
  type NumberingInput,
  type NumberingPreview,
  type NumberingSettings,
  type NumberPrefixSettings,
  type NumberScheme,
} from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import './finance.css';

const SCHEME_TEXT: Record<NumberScheme, string> = {
  year: 'Die Serie beginnt mit jedem Jahr neu bei der ersten Nummer. So war es bisher.',
  customer: 'Die Kundennummer des Rechnungsempfängers (seine Debitorennummer) steht in der Nummer. Die Serie läuft für alle Kunden gemeinsam ohne Unterbrechung weiter, auch über den Jahreswechsel.',
  plain: 'Nur die laufende Nummer, ohne Jahr. Die Serie läuft ohne Unterbrechung weiter, auch über den Jahreswechsel.',
};

type Starts = Record<DocumentNumberKind, string>;
const toStarts = (s: NumberingSettings): Starts => Object.fromEntries(NUMBER_KINDS.map((k) => [k, String(s.starts[k])])) as Starts;

/** Beispielnummer für die Kachel, mit dem ersten Wert der Rechnungsserie */
const example = (scheme: NumberScheme, start: number, prefix: string) => formatNumberByScheme(scheme, 'invoice_out', { year: new Date().getFullYear(), customerNumber: '10001', sequence: start, prefix });

/**
 * Einrichtung → Rechnungsnummern (Änderungswunsch vom 17.10.2026). Der Mandant wählt das Schema (Jahr, Kundennummer oder nur Nummernserie) und je Belegart die erste Nummer.
 * Unter dem Formular stehen die Hinweise, die sich mit jeder Änderung neu berechnen. Vor dem Speichern öffnet sich ein Fenster, das die neuen Nummern und alle Warnungen noch einmal
 * nennt. Gibt es schon ausgestellte Belege, speichert Wirkia nur nach ausdrücklicher Bestätigung. Ausgestellte Belege ändern sich nie.
 */
export function NumberingPage() {
  const { can } = useSession();
  const canWrite = can('setup.write');
  const client = useQueryClient();
  const dialog = useRef<HTMLDialogElement>(null);
  const settings = useQuery({ queryKey: ['numbering'], queryFn: async () => (await api<{ data: NumberingSettings }>('GET', '/finance/numbering')).data });
  const prefixes = useQuery({ queryKey: ['number-prefixes'], queryFn: async () => (await api<{ data: NumberPrefixSettings }>('GET', '/number-prefixes')).data });
  const [scheme, setScheme] = useState<NumberScheme>(DEFAULT_NUMBER_SCHEME);
  const [starts, setStarts] = useState<Starts>({ invoice_out: '1', credit_note: '1', cancellation: '1' });
  const [open, setOpen] = useState(false);
  const [understood, setUnderstood] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current || !settings.data) return;
    loaded.current = true;
    setScheme(settings.data.scheme);
    setStarts(toStarts(settings.data));
  }, [settings.data]);

  const parsed = NUMBER_KINDS.map((k) => Number(starts[k]));
  const valid = parsed.every((n) => Number.isInteger(n) && n >= 1 && n <= NUMBER_START_MAX);
  const body: NumberingInput | null = valid ? { scheme, starts: { invoice_out: parsed[0]!, credit_note: parsed[1]!, cancellation: parsed[2]! } } : null;
  const key = JSON.stringify(body);

  const preview = useQuery({
    queryKey: ['numbering-preview', key],
    enabled: !!body && !!settings.data,
    queryFn: async () => (await api<{ data: NumberingPreview }>('POST', '/finance/numbering/preview', body)).data,
    placeholderData: (previous) => previous,
  });

  const save = useMutation({
    mutationFn: async () => (await api<{ data: NumberingSettings }>('PUT', '/finance/numbering', { ...body, confirm: understood })).data,
    onSuccess: (s) => {
      client.setQueryData(['numbering'], s);
      client.invalidateQueries({ queryKey: ['numbering-preview'] });
      dialog.current?.close();
      setNotice({ ok: true, text: 'Die Nummerierung ist gespeichert. Sie gilt für alle Belege, die Sie ab jetzt ausstellen.' });
    },
    onError: (e) => {
      dialog.current?.close();
      setNotice({ ok: false, text: e instanceof ApiError ? e.message : 'Die Nummerierung konnte nicht gespeichert werden.' });
    },
  });

  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  if (settings.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!settings.data) return <div className="alert alert--error" role="alert">Die Nummerierung konnte nicht geladen werden.</div>;

  const saved = settings.data;
  const changed = scheme !== saved.scheme || NUMBER_KINDS.some((k) => Number(starts[k]) !== saved.starts[k]);
  const p = preview.data;
  const errors = (p?.warnings ?? []).filter((w) => w.level === 'error');
  const invoicePrefix = prefixes.data?.effective.invoice_out ?? 'RE';
  const startInvoice = Number(starts.invoice_out) || 1;

  return (
    <>
      <section className="card">
        <h2>Rechnungsnummern</h2>
        <p className="muted small">
          Wählen Sie, wie die Nummern Ihrer Rechnungen, Gutschriften und Stornorechnungen aufgebaut sind, und mit welcher Nummer jede Serie beginnt. Die Kennung der Belegart steht immer
          vorn, damit zwei Belege nie dieselbe Nummer tragen. Welche Kennung das ist (RE, GS, ST oder Ihre eigene), legen Sie unter <Link to="/einrichtung/nummernkennungen">Einrichtung → Nummernkennungen</Link> fest. Eine Änderung gilt nur für Belege, die Sie danach ausstellen.
        </p>
        {notice && <div className={`alert ${notice.ok ? 'alert--ok' : 'alert--error'}`} role={notice.ok ? 'status' : 'alert'}>{notice.text}</div>}

        <div className="plan-grid" role="radiogroup" aria-label="Schema der Rechnungsnummer">
          {NUMBER_SCHEMES.map((s) => (
            <label key={s} className={`plan-card card${scheme === s ? ' plan-card--on' : ''}`}>
              <input type="radio" name="scheme" value={s} checked={scheme === s} disabled={!canWrite} onChange={() => setScheme(s)} />
              <strong>{NUMBER_SCHEME_LABELS[s]}</strong>
              <span className="plan-card__price">{example(s, startInvoice, invoicePrefix)}</span>
              <span className="small">{SCHEME_TEXT[s]}</span>
            </label>
          ))}
        </div>

        <h3>Erste Nummer je Belegart</h3>
        <div className="form-grid">
          {NUMBER_KINDS.map((k) => (
            <label key={k} className="field">
              <span>{NUMBER_KIND_LABELS[k]} beginnen bei</span>
              <input className="input" type="number" inputMode="numeric" min={1} max={NUMBER_START_MAX} value={starts[k]} disabled={!canWrite} onChange={(e) => setStarts((s) => ({ ...s, [k]: e.target.value }))} aria-invalid={!valid} />
            </label>
          ))}
        </div>
        {!valid && <p className="field-error" role="alert">Bitte geben Sie je Belegart eine ganze Zahl ab 1 an.</p>}
        <p className="muted small">
          {scheme === 'year' ? 'Die erste Nummer gilt in jedem neuen Jahr.' : 'Die erste Nummer gilt einmal, danach läuft die Serie weiter.'} Liegt sie unter der letzten schon vergebenen Nummer der Serie, bleibt sie wirkungslos: Wirkia vergibt nie eine Nummer doppelt.
        </p>

        {p && (
          <>
            <h3>So sehen Ihre nächsten Nummern aus</h3>
            <ul className="plain-list">
              {p.next.map((n) => <li key={n.kind}>{n.label}: <strong>{n.number}</strong></li>)}
            </ul>
          </>
        )}

        <div className="numbering__notes" aria-live="polite">
          <h3>Wichtige Hinweise</h3>
          <p className="small">
            Ausgestellte Rechnungen sind nach den Regeln der digitalen Buchführung (GoBD) unveränderbar und behalten ihre Nummer. Jede Rechnungsnummer muss eindeutig sein und sich genau einer Rechnung zuordnen lassen.
            Eine Lücke in der Folge sollten Sie bei einer Prüfung erklären können. Stimmen Sie größere Änderungen, vor allem mitten im Jahr, mit Ihrem Steuerberater ab.
          </p>
          {p && p.issued && NUMBER_KINDS.some((k) => p.issued[k] > 0) && (
            <p className="small">
              Sie haben schon Belege ausgestellt: {NUMBER_KINDS.filter((k) => p.issued[k] > 0).map((k) => `${p.issued[k]} ${NUMBER_KIND_LABELS[k]}${p.last[k] ? ` (zuletzt ${p.last[k]})` : ''}`).join(', ')}.
            </p>
          )}
          {p && p.warnings.length > 0 && changed && (
            <ul className="numbering__warnings">
              {p.warnings.map((w, i) => (
                <li key={i} className={`alert ${w.level === 'error' ? 'alert--error' : w.level === 'warn' ? 'alert--warn' : 'alert'}`} role={w.level === 'error' ? 'alert' : 'note'}>{w.text}</li>
              ))}
            </ul>
          )}
        </div>

        {canWrite && (
          <div className="form-actions">
            <button type="button" className="btn btn--primary" disabled={!changed || !body || errors.length > 0 || preview.isFetching} onClick={() => { setNotice(null); setUnderstood(false); setOpen(true); }}>
              Nummerierung speichern …
            </button>
            {changed && <button type="button" className="btn btn--secondary" onClick={() => { setScheme(saved.scheme); setStarts(toStarts(saved)); }}>Zurücksetzen</button>}
          </div>
        )}
      </section>

      <dialog ref={dialog} className="dialog" aria-labelledby="numbering-title" onClose={() => setOpen(false)} onClick={(e) => { if (e.target === dialog.current && !save.isPending) setOpen(false); }}>
        <div className="dialog__body">
          <h2 id="numbering-title">Nummerierung ändern?</h2>
          <p>
            Ab jetzt stellt Wirkia Ihre Belege nach „{NUMBER_SCHEME_LABELS[scheme]}“ aus. Ausgestellte Belege behalten ihre Nummer.
          </p>
          {p && (
            <ul className="plain-list">
              {p.next.map((n) => <li key={n.kind}>Nächste Nummer, {n.label.toLowerCase()}: <strong>{n.number}</strong></li>)}
            </ul>
          )}
          {p && p.warnings.length > 0 && (
            <ul className="numbering__warnings">
              {p.warnings.map((w, i) => <li key={i} className={`alert ${w.level === 'error' ? 'alert--error' : w.level === 'warn' ? 'alert--warn' : 'alert'}`}>{w.text}</li>)}
            </ul>
          )}
          {p?.needsConfirm && (
            <label className="check">
              <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} disabled={save.isPending} />
              <span>Ich habe die Hinweise gelesen. Mir ist klar, dass es schon ausgestellte Belege gibt und dass ich die Nummerierung für neue Belege ändere.</span>
            </label>
          )}
          <div className="form-actions">
            <button type="button" className="btn btn--primary" disabled={save.isPending || (!!p?.needsConfirm && !understood)} onClick={() => save.mutate()}>
              {save.isPending ? 'Wird gespeichert …' : 'Nummerierung speichern'}
            </button>
            <button type="button" className="btn btn--secondary" disabled={save.isPending} onClick={() => setOpen(false)}>Abbrechen</button>
          </div>
        </div>
      </dialog>
    </>
  );
}
